/**
 * SOLID Review
 * S — Good. One concern: taking a seat — by a table code (join, rejoin, late join), by hosting a new
 *     table, or by the server's word that the player still has one (the cold rejoin).
 * O — Good. A new way to a seat is a plan, an answer and a builder.
 * L — N/A — functions.
 * I — Good. One function per decision on the contract.
 * D — Strong. The session draft, the route rule, FE1's lives builder, the schemas' types.
 */
import { decided, refuse, REFUSAL, type IEntityDecision, type TDecisionResult } from '../shared';
import { freshLives } from '../table/tableDraft';
import { smartJoinRoute, type IEntitySmartJoinIdentity } from '../../game/SmartJoinRoute';
import {
  draftOf, settleSession, withPlayer, joinedRoom, hostedRoom, inLobby, seated, joinFailed, type IConfigSettleSession, type IEntityRoomSource,
} from './sessionDraft';
import type { IPropsClientChanges, IPropsClientInput } from '../../ports/schemas/ClientChanges';
import type { IEntityClientGuest, IEntityClientPlayer } from '../../ports/schemas/ClientSession';
import type {
  ICommandJoinRouted, ICommandTableJoined, ICommandTableRejoined, ICommandTableLateJoined, ICommandTableHosted,
  ICommandActiveRoomFound, IEntityJoinRoute, IEntityProceed, IEntityRoundRead, IEntityColdRoom, IPayloadCodeLookup,
  IPayloadHost, IPayloadPlayer, IPayloadSeat, IPayloadRejoinRequest, IPayloadLateJoin,
} from '../../ports/schemas/SessionMoves';
import type { ICommandNothing } from '../../ports/schemas/TableMoves';

/**
 * TAKING A SEAT (frontend strict-ports plan, FE2, 2026-10-03) — useSmartJoinRouting, MainMenuPanel's
 * auto-host and useColdLoginRejoin, moved. The messages are theirs, word for word: the welcome screen
 * shows them. Every failure drops the pending code, which is what stopped the routing effect re-firing
 * on the same code forever; here it stops the entry asking again.
 */

type TSession<D> = TDecisionResult<IEntityDecision<IPropsClientChanges, D>>;

const UNREACHABLE = 'Could not reach the server. Please try again.';
const TABLE_NOT_FOUND = 'Table not found. Check the code and try again.';
const TABLE_FULL = 'That game is full (6 players). Try again later.';
const JOIN_FAILED = 'Could not join that table. Please try again.';
const REJOIN_FAILED = 'Could not rejoin that table. Please try again.';
/** The auto-host's words for a failure that was not the server's answer (MainMenuPanel's). */
const UNEXPECTED = 'Unexpected error.';

const NOTHING: IPropsClientChanges = {};
const STAY: IEntityProceed = { proceed: false };
const GO_ON: IEntityProceed = { proceed: true };
const NO_READ: IEntityRoundRead = { read: null };

function stopped(input: IPropsClientInput, message: string): TSession<IEntityJoinRoute> {
  const next = draftOf(input);
  joinFailed(next, message);
  const dto: IEntityJoinRoute = { route: { type: 'stop' } };
  return decided(settleSession(input, next), dto);
}

/** A pending code, for a known player: look it up, upper-cased as the routing hook did. */
export function decidePlanJoin(input: IPropsClientInput, _command: ICommandNothing): TSession<IPayloadCodeLookup> {
  const { session } = input;
  if (!session.pendingRoomCode || session.player === null) return refuse('No table code to join, or no player yet.', REFUSAL.NOT_READY);
  const request: IPayloadCodeLookup = { code: session.pendingRoomCode.trim().toUpperCase() };
  return decided(NOTHING, request);
}

/**
 * The code's room, routed by SmartJoinByCodeUseCase's rule: join a lobby, rejoin a seat that is this
 * player's (by id, email, or a suspended seat's name), late-join a game in progress under a name made
 * unique, or stop — no such room, a full one, or a lookup that failed.
 */
export function decideJoinRouted(input: IPropsClientInput, command: ICommandJoinRouted): TSession<IEntityJoinRoute> {
  const { session } = input;
  const player = session.player;
  if (player === null) return refuse('No player to seat.', REFUSAL.NOT_READY);
  if (command.failed) return stopped(input, UNREACHABLE);
  const identity: IEntitySmartJoinIdentity = { playerId: player.playerId, displayName: player.displayName, email: session.account.email };
  const route = smartJoinRoute(command.lookup, identity);
  if (route.type === 'NOT_FOUND') return stopped(input, TABLE_NOT_FOUND);
  if (route.type === 'ROOM_FULL') return stopped(input, TABLE_FULL);
  let dto: IEntityJoinRoute;
  if (route.type === 'JOIN') {
    const request: IPayloadSeat = { roomId: route.roomId, playerId: player.playerId };
    dto = { route: { type: 'JOIN', request } };
  } else if (route.type === 'REJOIN') {
    const request: IPayloadRejoinRequest = { roomCode: command.code, playerId: player.playerId, displayName: player.displayName, email: session.account.email };
    dto = { route: { type: 'REJOIN', request } };
  } else {
    const request: IPayloadLateJoin = { roomId: route.roomId, playerId: player.playerId, displayName: route.displayName, matchCredits: player.matchCredits };
    dto = { route: { type: 'LATE_JOIN', request } };
  }
  return decided(NOTHING, dto);
}

/** A lobby joined: seated at full lives, in the lobby. Every seat the server lists gets its lives. */
export function decideTableJoined(input: IPropsClientInput, command: ICommandTableJoined): TSession<IEntityProceed> {
  const { request, room } = command;
  const next = draftOf(input);
  if (room === null) {
    joinFailed(next, JOIN_FAILED);
    return decided(settleSession(input, next), STAY);
  }
  seated(next, joinedRoom(room, request.playerId));
  next.table.playerLives = freshLives(room.playerIds);
  inLobby(next);
  return decided(settleSession(input, next), GO_ON);
}

/**
 * A seat re-taken by code. The server matched it by email or name and answers with ITS player id, which
 * for a guest differs from this fresh tab's: the tab adopts it, or it is never recognised as that player
 * and a stale "new player" seat appears beside the resumed one. A guest's stored identity follows. Then
 * the live round is read, so the player lands on the real phase.
 */
export function decideTableRejoined(input: IPropsClientInput, command: ICommandTableRejoined): TSession<IEntityRoundRead> {
  const { session } = input;
  const { rejoin } = command;
  const player = session.player;
  if (player === null) return refuse('No player to seat.', REFUSAL.NOT_READY);
  const next = draftOf(input);
  if (rejoin === null) {
    joinFailed(next, REJOIN_FAILED);
    return decided(settleSession(input, next), NO_READ);
  }
  const effective = rejoin.playerId;
  let keep: IConfigSettleSession = {};
  if (effective !== player.playerId) {
    const adopted: IEntityClientPlayer = { ...player, playerId: effective };
    withPlayer(next, adopted);
    if (session.account.kind === 'guest') {
      next.session.account = { ...session.account, playerId: effective, displayName: player.displayName };
      const guest: IEntityClientGuest = { playerId: effective, displayName: player.displayName };
      keep = { guest };
    }
  }
  const source: IEntityRoomSource = { id: rejoin.roomId, roomCode: rejoin.roomCode, players: rejoin.players, status: rejoin.roomStatus };
  seated(next, joinedRoom(source, effective));
  const read: IEntityRoundRead = { read: { roomId: rejoin.roomId, playerId: effective } };
  return decided(settleSession(input, next, keep), read);
}

/** A game in progress joined late: seated, then the live round is read. */
export function decideTableLateJoined(input: IPropsClientInput, command: ICommandTableLateJoined): TSession<IEntityRoundRead> {
  const { request, room } = command;
  const next = draftOf(input);
  if (room === null) {
    joinFailed(next, JOIN_FAILED);
    return decided(settleSession(input, next), NO_READ);
  }
  seated(next, joinedRoom(room, request.playerId));
  const read: IEntityRoundRead = { read: { roomId: request.roomId, playerId: request.playerId } };
  return decided(settleSession(input, next), read);
}

/** The funnel asked to host: create the table, once — the intent is spent as the creation starts. */
export function decidePlanHost(input: IPropsClientInput, _command: ICommandNothing): TSession<IPayloadHost> {
  const { session } = input;
  if (!session.hostOnEntry || session.player === null) return refuse('Nothing to host, or no player yet.', REFUSAL.NOT_READY);
  const next = draftOf(input);
  next.session.hostOnEntry = false;
  next.session.hosting = true;
  next.session.hostError = null;
  const request: IPayloadHost = { hostPlayerId: session.player.playerId };
  return decided(settleSession(input, next), request);
}

/**
 * The new table. Created: the host sits in the lobby. Not created: the panel keeps showing, with the
 * server's own message, or "Unexpected error." when the failure was not the server's answer.
 */
export function decideTableHosted(input: IPropsClientInput, command: ICommandTableHosted): TSession<IEntityProceed> {
  const { request, room, serverMessage } = command;
  const next = draftOf(input);
  if (room === null) {
    next.session.hostError = serverMessage ?? UNEXPECTED;
    return decided(settleSession(input, next), STAY);
  }
  seated(next, hostedRoom(room, request.hostPlayerId));
  inLobby(next);
  next.session.hosting = false;
  next.session.hostError = null;
  return decided(settleSession(input, next), GO_ON);
}

/**
 * A registered player came back with nothing stored — the browser was closed — so the server is asked
 * whether they still have a live room. The overlay is up meanwhile.
 */
export function decidePlanColdRejoin(input: IPropsClientInput, _command: ICommandNothing): TSession<IPayloadPlayer> {
  const { session } = input;
  if (session.player === null) return refuse('No player to look up.', REFUSAL.NOT_READY);
  const next = draftOf(input);
  next.session.restoring = true;
  const request: IPayloadPlayer = { playerId: session.player.playerId };
  return decided(settleSession(input, next), request);
}

/**
 * The server's answer. No live room (or no answer): the overlay drops. A live room: resumed straight in,
 * no prompt — the server lists only a non-finished room this player is a member of.
 */
export function decideActiveRoomFound(input: IPropsClientInput, command: ICommandActiveRoomFound): TSession<IEntityColdRoom> {
  const { request, room } = command;
  if (room !== null) {
    const found: IEntityColdRoom = { record: { playerId: request.playerId, roomId: room.roomId, roomCode: room.roomCode } };
    return decided(NOTHING, found);
  }
  const next = draftOf(input);
  next.session.restoring = false;
  const none: IEntityColdRoom = { record: null };
  return decided(settleSession(input, next), none);
}
